// ============================================================
// MySQLJoinsAndRelationships.jsx
//
// Joins & Relationships (MySQL)
// Foundation · Module 04
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SchemaIcon from "@mui/icons-material/Schema";
import TableChartIcon from "@mui/icons-material/TableChart";
import LayersIcon from "@mui/icons-material/Layers";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import SpeedIcon from "@mui/icons-material/Speed";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map(l => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted }}>
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLJoinsAndRelationships() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 04"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    Joins & <span style={{ color: BRAND.gold }}>Relationships</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 780 }}>
                    Real databases are not flat files. Data lives in multiple related tables.
                    In this lesson you’ll learn how to model relationships correctly and
                    combine data using SQL JOINs — the skill that separates beginners from
                    real backend developers.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* WHY */}
                <SectionTitle icon={<LightbulbIcon />} label="Why Relationships Matter" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    Storing everything in one table causes duplication, inconsistency, and
                    update bugs. Relationships let you store data once and connect it
                    logically. JOINs are how SQL brings those connections back together.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="Core Relationship Concepts" />
                <Grid container spacing={2}>
                    {[
                        {
                            icon: <SchemaIcon />,
                            title: "One‑to‑Many",
                            description: "The most common relationship (e.g. one user → many orders). Uses a foreign key.",
                        },
                        {
                            icon: <LayersIcon />,
                            title: "Many‑to‑Many",
                            description: "Requires a junction table (e.g. students ↔ courses).",
                        },
                        {
                            icon: <TableChartIcon />,
                            title: "Foreign Keys",
                            description: "Foreign keys enforce valid relationships and prevent orphan records.",
                        },
                        {
                            icon: <CallMergeIcon />,
                            title: "JOIN Types",
                            description: "INNER JOIN, LEFT JOIN, RIGHT JOIN — each controls which rows are returned.",
                        },
                        {
                            icon: <SpeedIcon />,
                            title: "Join Performance",
                            description: "Indexes on foreign keys are essential for fast joins.",
                        },
                        {
                            icon: <CodeIcon />,
                            title: "Join Order",
                            description: "MySQL’s optimizer decides join order, but clean schema helps it choose well.",
                        },
                    ].map(c => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* PRACTICE */}
                <SectionTitle icon={<CodeIcon />} label="Hands‑On: Relationships & JOINs" />

                <CodeBlock
                    lines={[
                        {
                            comment: "One-to-many relationship: users → orders",
                            cmd: `CREATE TABLE users (
  id INT PRIMARY KEY AUTO_INCREMENT,
  name VARCHAR(100) NOT NULL
);

CREATE TABLE orders (
  id INT PRIMARY KEY AUTO_INCREMENT,
  user_id INT NOT NULL,
  total DECIMAL(8,2),
  FOREIGN KEY (user_id) REFERENCES users(id)
);`,
                        },
                    ]}
                />

                <CodeBlock
                    lines={[
                        {
                            comment: "Join users with their orders",
                            cmd: `SELECT u.name, o.total
FROM users u
JOIN orders o ON o.user_id = u.id;`,
                        },
                    ]}
                />

                <ExpandableExample title="LEFT JOIN vs INNER JOIN" icon={<CallMergeIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "INNER JOIN (only users with orders)",
                                cmd: `SELECT u.name, o.total
FROM users u
INNER JOIN orders o ON o.user_id = u.id;`,
                            },
                            {
                                comment: "LEFT JOIN (all users, even without orders)",
                                cmd: `SELECT u.name, o.total
FROM users u
LEFT JOIN orders o ON o.user_id = u.id;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="Many‑to‑Many with a Junction Table" icon={<LayersIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Students ↔ Courses (many-to-many)",
                                cmd: `CREATE TABLE students (
  id INT PRIMARY KEY AUTO_INCREMENT,
  name VARCHAR(100)
);

CREATE TABLE courses (
  id INT PRIMARY KEY AUTO_INCREMENT,
  title VARCHAR(100)
);

CREATE TABLE enrollments (
  student_id INT,
  course_id INT,
  PRIMARY KEY (student_id, course_id),
  FOREIGN KEY (student_id) REFERENCES students(id),
  FOREIGN KEY (course_id) REFERENCES courses(id)
);`,
                            },
                            {
                                comment: "Join across three tables",
                                cmd: `SELECT s.name, c.title
FROM students s
JOIN enrollments e ON e.student_id = s.id
JOIN courses c ON c.id = e.course_id;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "Why relational databases split data across tables",
                        "How one‑to‑many and many‑to‑many relationships work",
                        "How foreign keys enforce data integrity",
                        "How INNER JOIN and LEFT JOIN differ",
                        "How to design and query junction tables",
                        "Why indexing foreign keys matters for performance",
                    ].map(i => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{i}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER TIP */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> If a JOIN feels confusing, draw the tables
                        and arrows on paper. Visualizing relationships instantly clarifies SQL.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
